import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Linkedin, MessageSquare, Target, Briefcase, Users, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';
interface PageProps {
  openGetStarted?: () => void;
}
export function Services({
  openGetStarted
}: PageProps) {
  const services = [{
    id: 'resume',
    icon: <FileText size={40} data-id="element-521" />,
    title: 'Professional Resume Writing',
    desc: 'A powerful, ATS-optimized resume tailored to your specific industry and career goals. We highlight your achievements, not just your duties.',
    features: ['ATS Keyword Optimization', 'Modern, Clean Formatting', 'Achievement-Based Content', 'Industry-Specific Strategy']
  }, {
    id: 'linkedin',
    icon: <Linkedin size={40} data-id="element-522" />,
    title: 'LinkedIn Profile Optimization',
    desc: 'Transform your LinkedIn profile into a recruiter magnet. We optimize your headline, summary, and experience to increase your visibility in search results.',
    features: ['Keyword-Rich Headline', 'Compelling Summary', 'Skills Optimization', 'Networking Strategy']
  }, {
    id: 'cover-letter',
    icon: <MessageSquare size={40} data-id="element-523" />,
    title: 'Custom Cover Letters',
    desc: "A persuasive narrative that connects your experience to the specific needs of the employer, showing them exactly why you're the perfect fit.",
    features: ['Targeted Messaging', 'Compelling Opening', 'Value Proposition', 'Strong Call to Action']
  }, {
    id: 'executive',
    icon: <Briefcase size={40} data-id="element-524" />,
    title: 'Executive Branding',
    desc: 'Comprehensive career marketing for Directors, VPs, and C-Suite executives looking to position themselves as industry leaders.',
    features: ['Executive Summary', 'Board-Ready Formatting', 'Leadership Highlighting', 'Executive Bio']
  }, {
    id: 'coaching',
    icon: <Target size={40} data-id="element-525" />,
    title: 'Interview Coaching',
    desc: 'One-on-one sessions to help you master the STAR method, handle difficult questions, and negotiate your salary with confidence.',
    features: ['Mock Interviews', 'STAR Method Training', 'Salary Negotiation', 'Confidence Building']
  }, {
    id: 'career',
    icon: <Users size={40} data-id="element-526" />,
    title: 'Career Transition',
    desc: 'Specialized support for professionals looking to change industries or roles. We help translate your transferable skills effectively.',
    features: ['Skills Translation', 'New Industry Targeting', 'Narrative Shift', 'Gap Explanation']
  }];
  return <div className="w-full pt-20 pb-24" data-id="element-527">
      <SEO title="Career Services | Professional Resume Writers | Hirelio" description="Professional resume writing, ATS optimization, LinkedIn profile writing, executive resumes, cover letters, career coaching, and interview preparation." data-id="element-528" />
      {/* Header */}
      <div className="bg-navy-900 text-white py-24 relative overflow-hidden" data-id="element-529">
        <div className="absolute inset-0 bg-grid opacity-20" data-id="element-530"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-531">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6" data-id="element-532">
            Services Designed to Get You Hired
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-10" data-id="element-533">
            From ATS-optimized resumes to comprehensive interview coaching, we
            provide the tools you need to advance your career.
          </p>
          <button onClick={openGetStarted} className="bg-brand hover:bg-brand-600 text-white px-8 py-4 rounded-xl font-medium text-lg transition-all shadow-lg shadow-brand/25" data-id="element-534">
            Find Your Package
          </button>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-535">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-536">
          {services.map(service => <div key={service.id} className="bg-white dark:bg-navy-800 rounded-2xl p-8 border border-slate-200 dark:border-navy-700 hover:shadow-xl hover:border-brand/50 dark:hover:border-brand/50 transition-all group" data-id="element-537">
              <div className="text-brand mb-6 bg-brand-50 dark:bg-navy-900 w-16 h-16 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-538">
                {service.icon}
              </div>
              <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-4" data-id="element-539">
                {service.title}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed" data-id="element-540">
                {service.desc}
              </p>
              <ul className="space-y-2 mb-8" data-id="element-541">
                {service.features.map((feature, idx) => <li key={idx} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300" data-id="element-542">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand" data-id="element-543"></div>
                    {feature}
                  </li>)}
              </ul>
              <Link to={`/services/${service.id}`} className="text-brand font-medium flex items-center gap-2 group-hover:text-brand-600 transition-colors" data-id="element-544">
                Learn More{' '}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-545" />
              </Link>
            </div>)}
        </div>
      </div>
    </div>;
}